import type { ItineraryResponseDto, SpotResponseDto } from "@tabipla/shared";
import { isSameDay } from "./date";
import { parseTimeToMinutes, getCurrentMinutes } from "./time";

// Re-export time utilities for backward compatibility
export { parseTimeToMinutes } from "./time";

/**
 * Find today's itinerary from the list
 */
export function findTodaysItinerary(
  itineraries: ItineraryResponseDto[] | undefined,
  tripMode: "planning" | "traveling" | "settlement",
  today: Date = new Date()
): ItineraryResponseDto | null {
  if (!itineraries || tripMode !== "traveling") return null;

  return (
    itineraries.find((it) => {
      const itDate = new Date(it.date);
      return isSameDay(itDate, today);
    }) ?? null
  );
}

/**
 * Find the next spot based on current time
 */
export function findNextSpot(
  todaysItinerary: ItineraryResponseDto | null,
  currentTime: Date = new Date()
): SpotResponseDto | null {
  if (!todaysItinerary?.spots) return null;

  const currentMins = getCurrentMinutes(currentTime);

  // Sort spots by start time
  const sortedSpots = [...todaysItinerary.spots]
    .filter((spot) => spot.startTime)
    .sort((a, b) => parseTimeToMinutes(a.startTime!) - parseTimeToMinutes(b.startTime!));

  // Find the next spot (start time is in the future)
  for (const spot of sortedSpots) {
    const spotMinutes = parseTimeToMinutes(spot.startTime!);
    if (spotMinutes > currentMins) {
      return spot;
    }
  }

  // If no future spots, return null
  return null;
}

/**
 * Get coordinates for nearby suggestions when adding a spot
 */
export function getDestinationCoords(
  itineraryId: string | null,
  itineraries: ItineraryResponseDto[] | undefined
): { latitude: number; longitude: number } | null {
  if (!itineraryId || !itineraries) return null;

  // Find the itinerary for the selected day
  const itinerary = itineraries.find((it) => it.id === itineraryId);
  if (!itinerary?.spots?.length) return null;

  // Get the last spot with coordinates
  const spotsWithCoords = itinerary.spots.filter(
    (spot) => spot.latitude != null && spot.longitude != null
  );
  if (spotsWithCoords.length === 0) return null;

  const lastSpot = spotsWithCoords[spotsWithCoords.length - 1];
  return {
    latitude: lastSpot.latitude!,
    longitude: lastSpot.longitude!,
  };
}

/**
 * Get badge style based on trip status
 */
export function getStatusBadgeStyle(status: string): string {
  switch (status) {
    case "planning":
      return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300";
    case "confirmed":
      return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
  }
}

/**
 * Get badge style based on group role (for tabitto)
 */
export function getRoleBadgeStyle(role: string): string {
  switch (role) {
    case "owner":
      return "bg-primary/10 text-primary";
    case "editor":
      return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
  }
}

/**
 * Determine if a member can be modified (not owner)
 */
export function canModifyMember(role: string): boolean {
  return role !== "owner";
}
